import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CardElement, useStripe, useElements, Elements } from "@stripe/react-stripe-js";
import { useAdminApi, toLocalDateStr, fmtMoney, fmtDate, exportXLSX, PASS_LABELS } from "../adminUtils";
import { useStudio } from "../../../studios/StudioContext";
import { getStripe } from "../../../components/StripeCardForm";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-students.css";
import "../admin-misc.css";
import "../admin-responsive.css";

// Sales & Merch tab.
//   Sell  — owners and teachers: one cart for merch, class passes and memberships; cash, card typed
//           here, the student's own phone (QR code), or comp (owners). Every sale records who sold it
//           and emails a receipt. Sales tax applies to taxable merch only.
//   Sales — paid sales by date (owners: everyone's, with seller; teachers: their own).
//   Stock — owners: products, prices, stock.

const PAY_LABEL = { cash: "Cash", card: "Card", phone: "On their phone", comp: "Comp" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const toC = d => Math.round(Number(d || 0) * 100);

async function api(API, AUTH, path, opts = {}) {
  const res = await fetch(`${API}${path}`, { headers: AUTH, ...opts, body: opts.body ? JSON.stringify(opts.body) : undefined });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ── Card form (Stripe Elements, card typed on this screen) ──
const CARD_OPTIONS = {
  hidePostalCode: true,
  style: { base: { fontSize: "16px", color: "#2d2d3e", "::placeholder": { color: "#9b8fa8" } }, invalid: { color: "#c53030" } },
};

function CardPayInner({ clientSecret, total, name, email, onPaid, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const pay = async () => {
    if (!stripe || !elements || paying) return;
    setPaying(true); setError("");
    try {
      const { error: err, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { ...(name ? { name } : {}), ...(email ? { email } : {}) },
        },
      });
      if (err) throw new Error(err.message);
      await onPaid(paymentIntent.id);
    } catch (e) {
      setError(e.message || "The card didn't go through.");
      setPaying(false);
    }
  };

  return (
    <div>
      <div style={{ border: "1.5px solid var(--color-grey-300)", borderRadius: 6, padding: "0.8rem 0.9rem", background: "#fff", maxWidth: 440 }}>
        <CardElement options={CARD_OPTIONS} />
      </div>
      {!stripe && <p className="adm-hint">Loading the card form…</p>}
      {error && <p className="adm-error">{error}</p>}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
        <button className="adm-btn adm-btn--ghost" onClick={onCancel} disabled={paying}>Cancel sale</button>
        <button className="adm-btn adm-btn--primary" onClick={pay} disabled={!stripe || paying}>
          {paying ? "Charging…" : `Charge ${fmtMoney(total)}`}
        </button>
      </div>
    </div>
  );
}

function CardPay(props) {
  const { stripePublishableKey } = useStudio();
  if (!stripePublishableKey) return <p className="adm-error">Card payments aren't set up on this site (no Stripe key).</p>;
  return <Elements stripe={getStripe(stripePublishableKey)}><CardPayInner {...props} /></Elements>;
}

// ── Student picker ──────────────────────────────────────────
function StudentPicker({ student, setStudent }) {
  const { API, AUTH } = useAdminApi();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(() => {
      api(API, AUTH, `/sales/students?q=${encodeURIComponent(q.trim())}`)
        .then(d => setResults(d.students || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, API, AUTH]);

  const pick = s => { setStudent({ name: s.name, email: s.email, passes: s.passes }); setQ(""); setResults([]); };
  const passLine = ps => (ps || []).map(p => `${PASS_LABELS[p.type] ?? p.type}${p.left === null ? "" : ` · ${p.left} left`}`).join(", ");

  return (
    <div>
      <p className="adm-form-title" style={{ marginBottom: "0.5rem" }}>Student</p>
      <input className="adm-input" placeholder="Find a student by name or email" value={q} onChange={e => setQ(e.target.value)} />
      {results.length > 0 && (
        <div style={{ border: "1px solid var(--color-grey-200, #e5e5e5)", borderRadius: 6, marginTop: 4, background: "#fff" }}>
          {results.map(s => (
            <button key={s.email} onClick={() => pick(s)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "0.5rem 0.75rem", border: "none", borderBottom: "1px solid var(--color-grey-100, #f0f0f0)", background: "none", cursor: "pointer" }}>
              <strong>{s.name}</strong> <span style={{ color: "var(--color-grey-500)" }}>{s.email}</span>
              {s.passes?.length > 0 && <div style={{ fontSize: "0.8rem", color: "var(--color-grey-500)" }}>Has: {passLine(s.passes)}</div>}
            </button>
          ))}
        </div>
      )}
      <div className="adm-form-grid" style={{ marginTop: "0.6rem" }}>
        <label className="adm-form-label">Name
          <input className="adm-input" value={student.name} onChange={e => setStudent(s => ({ ...s, name: e.target.value, passes: undefined }))} />
        </label>
        <label className="adm-form-label">Email (gets the receipt)
          <input className="adm-input" type="email" value={student.email} onChange={e => setStudent(s => ({ ...s, email: e.target.value, passes: undefined }))} />
        </label>
      </div>
      {student.passes?.length > 0 && <p className="adm-hint">Already has: {passLine(student.passes)}</p>}
    </div>
  );
}

// ── Sell ────────────────────────────────────────────────────
function SellSection({ isOwner }) {
  const { API, AUTH } = useAdminApi();
  const studio = useStudio();
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [cart, setCart] = useState([]);              // { key, kind, id, quantity }
  const [student, setStudent] = useState({ name: "", email: "" });
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [phase, setPhase] = useState("cart");        // cart | card | phone | done
  const [order, setOrder] = useState(null);
  const [pay, setPay] = useState(null);              // { clientSecret } | { qr, checkoutUrl }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receiptEmail, setReceiptEmail] = useState("");
  const [copied, setCopied] = useState(false);

  const loadCatalog = useCallback(() => {
    api(API, AUTH, "/sales/catalog").then(setCatalog).catch(e => setLoadError(e.message));
  }, [API, AUTH]);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const find = useCallback((kind, id) => {
    if (!catalog) return null;
    if (kind === "pass") return catalog.passes.find(p => p.id === id);
    if (kind === "membership") return catalog.memberships.find(m => m.id === id);
    return catalog.products.find(p => p.id === id);
  }, [catalog]);

  const hasMembership = cart.some(i => i.kind === "membership");
  const byCard = method === "card" || method === "phone";

  // Preview of the server's pricing (the server works it out again and is the one that counts).
  const lines = useMemo(() => cart.map(i => {
    const c = find(i.kind, i.id) || {};
    let unit = 0;
    if (i.kind === "merch") unit = c.price;
    else if (i.kind === "pass") unit = byCard ? c.card : c.cash;
    else unit = c.card;
    if (method === "comp") unit = 0;
    const name = i.kind === "membership" ? `${c.label} membership (first month)` : (c.label || c.name || "?");
    return { ...i, name, unit, total: toC(unit) * i.quantity / 100, taxable: i.kind === "merch" && c.taxable && method !== "comp", stock: c.stock };
  }), [cart, find, byCard, method]);
  const subtotalC = lines.reduce((t, l) => t + toC(l.unit) * l.quantity, 0);
  const taxC = Math.round(lines.reduce((t, l) => t + (l.taxable ? toC(l.unit) * l.quantity : 0), 0) * (catalog?.taxRate ?? 0));
  const total = (subtotalC + taxC) / 100;

  const add = (kind, id) => {
    setError("");
    if (kind === "membership") {
      if (cart.length && !hasMembership) { setError("Sell a membership on its own: finish or clear this sale first."); return; }
      setCart([{ key: `membership:${id}`, kind, id, quantity: 1 }]);
      if (method === "cash" || method === "comp") setMethod("card");
      return;
    }
    if (hasMembership) { setError("A membership is sold on its own: finish that sale first, or remove it."); return; }
    setCart(c => {
      const key = `${kind}:${id}`;
      const found = c.find(x => x.key === key);
      if (found) return c.map(x => x.key === key ? { ...x, quantity: x.quantity + 1 } : x);
      return [...c, { key, kind, id, quantity: 1 }];
    });
  };
  const setQty = (key, q) => setCart(c => q < 1 ? c.filter(x => x.key !== key) : c.map(x => x.key === key ? { ...x, quantity: q } : x));

  const reset = () => {
    setCart([]); setStudent({ name: "", email: "" }); setMethod("cash"); setNote("");
    setPhase("cart"); setOrder(null); setPay(null); setError(""); setReceiptEmail(""); setCopied(false);
    loadCatalog();
  };

  const needsStudent = cart.some(i => i.kind !== "merch");
  const emailOk = !student.email.trim() || EMAIL_RE.test(student.email.trim());
  const studentOk = !needsStudent || (student.name.trim().length >= 2 && EMAIL_RE.test(student.email.trim()));
  const stockOk = lines.every(l => l.kind !== "merch" || l.quantity <= (l.stock ?? 0));
  const canCharge = cart.length > 0 && emailOk && studentOk && stockOk && !busy && (!byCard || total >= 0.5);

  const checkout = async () => {
    setBusy(true); setError("");
    try {
      const d = await api(API, AUTH, "/sales/checkout", { method: "POST", body: {
        location: studio?.studioId, returnUrl: studio?.siteUrl,
        student: { name: student.name.trim(), email: student.email.trim() },
        items: cart.map(i => ({ kind: i.kind, id: i.id, quantity: i.quantity })),
        paymentMethod: method, note,
      }});
      setOrder(d.order);
      if (d.order.status === "paid") { setPhase("done"); return; }
      if (d.clientSecret) { setPay({ clientSecret: d.clientSecret }); setPhase("card"); return; }
      if (d.qr) { setPay({ qr: d.qr, checkoutUrl: d.checkoutUrl }); setPhase("phone"); return; }
      throw new Error("Unexpected reply from the server.");
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const cardPaid = async paymentIntentId => {
    try {
      const d = await api(API, AUTH, `/sales/orders/${order.id}/complete`, { method: "POST", body: { paymentIntentId } });
      setOrder(d.order); setPhase("done");
    } catch {
      setPhase("confirming");   // the card went through; keep checking until the sale is recorded
    }
  };

  const cancelSale = async () => {
    if (!order) { setPhase("cart"); return; }
    try {
      const d = await api(API, AUTH, `/sales/orders/${order.id}/cancel`, { method: "POST" });
      if (d.order.status === "paid") { setOrder(d.order); setPhase("done"); return; }
    } catch { /* leave it pending; Stripe expires it */ }
    setOrder(null); setPay(null); setPhase("cart");
  };

  // Student paying on their phone, or a card payment still being recorded: check every 3 seconds.
  useEffect(() => {
    if ((phase !== "phone" && phase !== "confirming") || !order) return;
    const t = setInterval(async () => {
      try {
        const d = await api(API, AUTH, `/sales/orders/${order.id}`);
        if (d.order.status === "paid") { setOrder(d.order); setPhase("done"); }
        else if (d.order.status === "cancelled") { setError("The payment link expired. Start the sale again."); setOrder(null); setPay(null); setPhase("cart"); }
      } catch { /* keep trying */ }
    }, 3000);
    return () => clearInterval(t);
  }, [phase, order, API, AUTH]);

  useEffect(() => {
    if (phase === "done" && order) setReceiptEmail(order.student?.email || "");
  }, [phase, order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership: Stripe confirms a few seconds after the card goes through and the member code is
  // made then. Keep checking for ~20 seconds so the code shows on this screen.
  const waitingForMemberCode = phase === "done" && !!order &&
    order.items.some(i => i.kind === "membership" && !(i.passCodes || []).length);
  useEffect(() => {
    if (!waitingForMemberCode) return;
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      try {
        const d = await api(API, AUTH, `/sales/orders/${order.id}`);
        if (d.order.items.some(i => i.kind === "membership" && (i.passCodes || []).length)) { setOrder(d.order); clearInterval(t); }
      } catch { /* keep trying */ }
      if (tries >= 10) clearInterval(t);
    }, 2000);
    return () => clearInterval(t);
  }, [waitingForMemberCode, order?.id, API, AUTH]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendReceipt = async () => {
    setBusy(true); setError("");
    try {
      const d = await api(API, AUTH, `/sales/orders/${order.id}/receipt`, { method: "POST", body: { email: receiptEmail.trim() } });
      setOrder(d.order);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (loadError) return <p className="adm-error">Couldn't load what's for sale: {loadError}</p>;
  if (!catalog) return <p className="adm-loading">Loading…</p>;

  // ── Done ──
  if (phase === "done" && order) {
    const codes = order.items.flatMap(i => (i.passCodes || []).map(c => ({ c, name: i.name })));
    const membershipPending = order.items.some(i => i.kind === "membership" && !(i.passCodes || []).length);
    return (
      <div className="adm-create-form" style={{ maxWidth: 560 }}>
        <div style={{ background: "#d4f7e7", border: "1px solid #2e7d52", borderRadius: 6, padding: "0.9rem 1rem" }}>
          <strong style={{ color: "#1a6640", fontSize: "1.05rem" }}>✓ Paid: {fmtMoney(order.total)} · {PAY_LABEL[order.paymentMethod]}</strong>
          <div style={{ fontSize: "0.85rem", color: "#2e7d52", marginTop: "0.25rem" }}>
            Receipt {order.receiptNo}{order.student?.name ? ` · ${order.student.name}` : ""}
          </div>
          {codes.length > 0 && (
            <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px solid #9fd4b8" }}>
              <div style={{ fontSize: "0.8rem", color: "#1a6640", fontWeight: 600 }}>{codes.length > 1 ? "Pass codes" : order.items.some(i => i.kind === "membership") ? "Member code" : "Pass code"} ({order.items.some(i => i.kind === "membership") ? "also in their welcome email" : "also in their receipt email"})</div>
              {codes.map(({ c, name }) => (
                <div key={c} style={{ marginTop: "0.35rem" }}>
                  <span style={{ fontSize: "1.6rem", fontWeight: 800, letterSpacing: "0.06em", color: "#1a6640", userSelect: "all" }}>{c}</span>
                  <span style={{ marginLeft: "0.6rem", fontSize: "0.85rem", color: "#2e7d52" }}>{name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {membershipPending && <p className="adm-hint">Waiting for Stripe to confirm the membership: the member code appears here in a few seconds (it's also emailed to them).</p>}
        <div style={{ marginTop: "1rem" }}>
          {order.receiptSentAt
            ? <p className="adm-saved" style={{ margin: 0 }}>✓ Receipt emailed to {order.student.email}</p>
            : (
              <div>
                <p className="adm-hint" style={{ marginTop: 0 }}>{order.receiptError ? `The receipt didn't send (${order.receiptError}).` : "No email on this sale, so no receipt was sent."} Email one:</p>
                <div style={{ display: "flex", gap: "0.5rem", maxWidth: 440 }}>
                  <input className="adm-input" type="email" placeholder="their@email.com" value={receiptEmail} onChange={e => setReceiptEmail(e.target.value)} />
                  <button className="adm-btn adm-btn--sm" disabled={busy || !EMAIL_RE.test(receiptEmail.trim())}
                    onClick={sendReceipt}>Send</button>
                </div>
              </div>
            )}
        </div>
        {error && <p className="adm-error">{error}</p>}
        <button className="adm-btn adm-btn--primary" style={{ marginTop: "1.25rem" }} onClick={reset}>New sale</button>
      </div>
    );
  }

  // ── Card / phone ──
  if (phase === "confirming" && order) {
    return (
      <div className="adm-create-form" style={{ maxWidth: 560 }}>
        <p className="adm-form-title">Card charged: {fmtMoney(order.total)}</p>
        <p className="adm-loading">Recording the sale… (this page updates by itself)</p>
      </div>
    );
  }

  if ((phase === "card" || phase === "phone") && order) {
    return (
      <div className="adm-create-form" style={{ maxWidth: 560 }}>
        <p className="adm-form-title">{phase === "card" ? "Card payment" : "Student pays on their phone"}: {fmtMoney(order.total)}</p>
        <p className="adm-hint" style={{ marginTop: 0 }}>
          {order.items.map(i => `${i.name}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`).join(", ")}
          {order.student?.name ? ` · ${order.student.name}` : ""}
        </p>
        {phase === "card" && (
          <CardPay clientSecret={pay.clientSecret} total={order.total} name={order.student?.name} email={order.student?.email}
            onPaid={cardPaid} onCancel={cancelSale} />
        )}
        {phase === "phone" && (
          <div>
            <img src={pay.qr} alt="Payment QR code" width={260} height={260}
              style={{ display: "block", width: 260, height: 260, maxWidth: "none", aspectRatio: "1 / 1", objectFit: "contain", background: "#fff", border: "1px solid var(--color-grey-200, #e5e5e5)", borderRadius: 8 }} />
            <p className="adm-hint">Ask them to scan this with their phone camera and pay. This page updates by itself when they've paid. The link works for 30 minutes.</p>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button className="adm-btn adm-btn--ghost" onClick={cancelSale}>Cancel sale</button>
              <button className="adm-btn adm-btn--ghost" onClick={async () => {
                try { await navigator.clipboard.writeText(pay.checkoutUrl); setCopied(true); } catch { setCopied(false); }
              }}>{copied ? "✓ Link copied" : "Copy payment link"}</button>
            </div>
            <p className="adm-loading">Waiting for payment…</p>
          </div>
        )}
        {error && <p className="adm-error">{error}</p>}
      </div>
    );
  }

  // ── Cart ──
  const groups = catalog.products.reduce((g, p) => { (g[p.category] = g[p.category] || []).push(p); return g; }, {});
  const tile = (key, onClick, title, price, sub, disabled) => (
    <button key={key} onClick={onClick} disabled={disabled}
      style={{ textAlign: "left", padding: "0.6rem 0.75rem", borderRadius: 8, border: "1.5px solid var(--color-grey-300)", background: "#fff", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.45 : 1 }}>
      <div style={{ fontWeight: 600 }}>{title}</div>
      <div style={{ fontSize: "0.85rem", color: "var(--color-tertiary)", fontWeight: 600 }}>{price}</div>
      {sub && <div style={{ fontSize: "0.75rem", color: "var(--color-grey-500)" }}>{sub}</div>}
    </button>
  );
  const tiles = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "0.5rem", marginBottom: "1.25rem" };

  const methods = ["cash", "card", "phone", ...(isOwner ? ["comp"] : [])];
  const chargeLabel = {
    cash: `Take ${fmtMoney(total)} cash`, card: `Charge ${fmtMoney(total)} by card`,
    phone: "Show payment QR code", comp: "Record as comp (free)",
  }[method];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.5rem", alignItems: "start" }}>
      {/* What's for sale */}
      <div>
        <h2 className="adm-section-title" style={{ marginBottom: "0.5rem" }}>Classes &amp; passes</h2>
        <div style={tiles}>
          {catalog.passes.map(p => tile(p.id, () => add("pass", p.id), p.label,
            byCard ? fmtMoney(p.card) : fmtMoney(p.cash),
            p.card !== p.cash ? (byCard ? `${fmtMoney(p.cash)} cash` : `${fmtMoney(p.card)} by card`) : null))}
        </div>
        {catalog.memberships.length > 0 && (
          <>
            <h2 className="adm-section-title" style={{ marginBottom: "0.5rem" }}>Memberships</h2>
            <div style={tiles}>
              {catalog.memberships.map(m => tile(m.id, () => add("membership", m.id), m.label,
                `${fmtMoney(m.card)}/month`, `${m.sub || ""}${m.sub ? " · " : ""}card only, renews monthly`))}
            </div>
          </>
        )}
        <h2 className="adm-section-title" style={{ marginBottom: "0.5rem" }}>Merchandise</h2>
        {catalog.products.length === 0 && <p className="adm-empty" style={{ textAlign: "left" }}>No products yet{isOwner ? " (add them under Stock)" : ""}.</p>}
        {Object.entries(groups).map(([cat, ps]) => (
          <div key={cat}>
            {Object.keys(groups).length > 1 && <p style={{ margin: "0 0 0.35rem", fontSize: "0.8rem", fontWeight: 600, color: "var(--color-grey-500)" }}>{cat}</p>}
            <div style={tiles}>
              {ps.map(p => tile(p.id, () => add("merch", p.id), p.name, fmtMoney(p.price),
                p.stock > 0 ? `${p.stock} in stock${p.taxable ? "" : " · no tax"}` : "Out of stock", p.stock <= 0))}
            </div>
          </div>
        ))}
      </div>

      {/* Cart + payment */}
      <div className="adm-create-form" style={{ margin: 0 }}>
        <p className="adm-form-title">Sale</p>
        {cart.length === 0 && <p className="adm-empty" style={{ textAlign: "left" }}>Tap what they're buying.</p>}
        {lines.map(l => (
          <div key={l.key} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0", borderBottom: "1px solid var(--color-grey-100, #f0f0f0)" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{l.name}</div>
              <div style={{ fontSize: "0.8rem", color: "var(--color-grey-500)" }}>
                {fmtMoney(l.unit)} each{l.taxable ? " + tax" : ""}
                {l.kind === "merch" && l.quantity > (l.stock ?? 0) && <span style={{ color: "#c53030" }}> · only {l.stock} in stock</span>}
              </div>
            </div>
            {l.kind !== "membership" && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <button className="adm-btn-ghost-sm" onClick={() => setQty(l.key, l.quantity - 1)} aria-label="One less">−</button>
                <span style={{ minWidth: 22, textAlign: "center" }}>{l.quantity}</span>
                <button className="adm-btn-ghost-sm" onClick={() => setQty(l.key, l.quantity + 1)} aria-label="One more">+</button>
              </div>
            )}
            <strong style={{ minWidth: 70, textAlign: "right" }}>{fmtMoney(l.total)}</strong>
            <button className="adm-email-remove" onClick={() => setQty(l.key, 0)} aria-label={`Remove ${l.name}`}>✕</button>
          </div>
        ))}
        {cart.length > 0 && (
          <div style={{ margin: "0.6rem 0 1rem", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{fmtMoney(subtotalC / 100)}</span></div>
            {taxC > 0 && <div style={{ display: "flex", justifyContent: "space-between", color: "var(--color-grey-500)" }}>
              <span>Sales tax {(catalog.taxRate * 100).toFixed(1).replace(/\.0$/, "")}% (merch)</span><span>{fmtMoney(taxC / 100)}</span></div>}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: "1.1rem", marginTop: "0.25rem" }}>
              <span>Total</span><span style={{ color: "var(--color-tertiary)" }}>{fmtMoney(total)}</span></div>
          </div>
        )}

        <StudentPicker student={student} setStudent={setStudent} />
        {needsStudent && !studentOk && <p className="adm-hint">Passes and memberships need the student's name and email.</p>}
        {!emailOk && <p className="adm-error">That email doesn't look right.</p>}

        <p className="adm-form-title" style={{ margin: "1rem 0 0.5rem" }}>Payment</p>
        <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
          {methods.map(m => {
            const off = hasMembership && (m === "cash" || m === "comp");
            return (
              <button key={m} disabled={off} onClick={() => setMethod(m)}
                className={`adm-btn ${method === m ? "adm-btn--primary" : "adm-btn--ghost"}`}
                style={{ padding: "0.5rem 0.9rem", opacity: off ? 0.4 : 1 }}>{PAY_LABEL[m]}</button>
            );
          })}
        </div>
        <p className="adm-hint">
          {method === "cash" && "Passes are at the cash price."}
          {method === "card" && "Type the card into the form on the next screen. Passes are at the card price, same as online."}
          {method === "phone" && "Shows a QR code: they pay on their own phone (Apple Pay works). Passes are at the card price."}
          {method === "comp" && "Free: nothing is charged. Comp passes don't count as revenue."}
          {hasMembership && " Memberships renew monthly on the card used."}
        </p>
        <label className="adm-form-label">Note (optional)
          <input className="adm-input" value={note} onChange={e => setNote(e.target.value)} placeholder={method === "comp" ? "Why it's free" : ""} />
        </label>

        {error && <p className="adm-error">{error}</p>}
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
          {cart.length > 0 && <button className="adm-btn adm-btn--ghost" onClick={reset} disabled={busy}>Clear</button>}
          <button className="adm-btn adm-btn--primary" onClick={checkout} disabled={!canCharge}>
            {busy ? "Working…" : chargeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sales history ───────────────────────────────────────────
function SalesSection({ isOwner }) {
  const { API, AUTH } = useAdminApi();
  const today = toLocalDateStr(new Date());
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resent, setResent] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setOrders((await api(API, AUTH, `/sales/orders?from=${from}&to=${to}`)).orders || []); }
    catch (e) { setError(e.message); setOrders([]); }
    finally { setLoading(false); }
  }, [API, AUTH, from, to]);
  useEffect(() => { load(); }, [load]);

  const resend = async o => {
    try { await api(API, AUTH, `/sales/orders/${o.id}/receipt`, { method: "POST", body: {} }); setResent(o.id); load(); }
    catch (e) { setError(e.message); }
  };

  const sum = f => orders.reduce((t, o) => t + (f(o) || 0), 0);
  const byMethod = ["cash", "card", "phone", "comp"].map(m => [m, sum(o => o.paymentMethod === m ? o.total : 0), orders.filter(o => o.paymentMethod === m).length]).filter(x => x[2] > 0);
  const bySeller = Object.values(orders.reduce((g, o) => {
    const k = o.soldBy?.name || "?";
    g[k] = g[k] || { name: k, total: 0, count: 0 };
    g[k].total += o.total; g[k].count += 1; return g;
  }, {}));
  const itemsText = o => o.items.map(i => `${i.name}${i.quantity > 1 ? ` × ${i.quantity}` : ""}`).join(", ");
  const time = o => new Date(o.paidAt || o.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  const exportRows = () => exportXLSX("Sales", orders.map(o => ({
    Date: o.date, Time: time(o), Receipt: o.receiptNo, Student: o.student?.name || "", Email: o.student?.email || "",
    Items: itemsText(o), Subtotal: o.subtotal, Tax: o.tax, Total: o.total, Payment: PAY_LABEL[o.paymentMethod],
    "Sold by": o.soldBy?.name || "", Site: o.location || "", Note: o.note || "",
  })), `sales-${from}-to-${to}.xlsx`);

  return (
    <div>
      <div className="adm-roster-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
        <h2 className="adm-section-title">{isOwner ? "Sales" : "My sales"}</h2>
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <input className="adm-input" type="date" value={from} onChange={e => setFrom(e.target.value)} style={{ width: "auto" }} />
          <span>to</span>
          <input className="adm-input" type="date" value={to} onChange={e => setTo(e.target.value)} style={{ width: "auto" }} />
          {isOwner && orders.length > 0 && <button className="adm-btn adm-btn--sm" onClick={exportRows}>Export</button>}
        </div>
      </div>

      {!loading && orders.length > 0 && (
        <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", margin: "0.5rem 0 1rem", fontSize: "0.9rem" }}>
          <div><strong>{fmtMoney(sum(o => o.total))}</strong> total · {orders.length} sale{orders.length !== 1 ? "s" : ""}
            {sum(o => o.tax) > 0 ? ` · ${fmtMoney(sum(o => o.tax))} sales tax` : ""}</div>
          <div>{byMethod.map(([m, t, n]) => `${PAY_LABEL[m]} ${fmtMoney(t)} (${n})`).join(" · ")}</div>
          {isOwner && bySeller.length > 0 && <div>By seller: {bySeller.map(s => `${s.name} ${fmtMoney(s.total)} (${s.count})`).join(" · ")}</div>}
        </div>
      )}

      {loading && <p className="adm-loading">Loading…</p>}
      {error && <p className="adm-error">{error}</p>}
      {!loading && orders.length === 0 && <p className="adm-empty">No sales {from === to ? (from === today ? "today" : `on ${fmtDate(from)}`) : "in these dates"}.</p>}
      {!loading && orders.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>{from === to ? "Time" : "Date"}</th><th>Receipt</th><th>Student</th><th>Items</th><th>Payment</th><th>Total</th>{isOwner && <th>Sold by</th>}<th>Receipt email</th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td>{from === to ? time(o) : fmtDate(o.date)}</td>
                  <td><code>{o.receiptNo}</code></td>
                  <td>{o.student?.name || o.student?.email || "—"}</td>
                  <td>{itemsText(o)}{o.items.some(i => i.passCodes?.length) && (
                    <div style={{ fontSize: "0.8rem", color: "var(--color-grey-500)" }}>{o.items.flatMap(i => i.passCodes || []).join(", ")}</div>)}
                    {o.note && <div style={{ fontSize: "0.8rem", color: "var(--color-grey-500)" }}>{o.note}</div>}</td>
                  <td><span className="adm-type-badge">{PAY_LABEL[o.paymentMethod]}</span></td>
                  <td><strong>{fmtMoney(o.total)}</strong>{o.tax > 0 && <div style={{ fontSize: "0.75rem", color: "var(--color-grey-500)" }}>incl. {fmtMoney(o.tax)} tax</div>}</td>
                  {isOwner && <td>{o.soldBy?.name || "—"}</td>}
                  <td>
                    {o.receiptSentAt ? (resent === o.id ? "✓ Sent again" : "✓ Sent") : (o.student?.email ? "Not sent" : "No email")}
                    {o.student?.email && <div><button className="adm-btn-ghost-sm" onClick={() => resend(o)}>Resend</button></div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Stock (owners) ──────────────────────────────────────────
const EMPTY_PRODUCT = { name: "", category: "", price: "", cost: "", stock: "", taxable: true };

function StockSection() {
  const { API, AUTH } = useAdminApi();
  const [products,    setProducts]   = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [showForm,    setShowForm]   = useState(false);
  const [editTarget,  setEditTarget] = useState(null);
  const [form,        setForm]       = useState(EMPTY_PRODUCT);
  const [saving,      setSaving]     = useState(false);
  const [error,       setError]      = useState("");

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/merch/products`, { headers: AUTH });
      const data = await res.json();
      setProducts(data.products ?? []);
    } catch { setProducts([]); }
    finally   { setLoading(false); }
  }, [API, AUTH]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_PRODUCT); setError(""); setShowForm(true); };
  const openEdit = p => {
    setEditTarget(p);
    setForm({ name: p.name, category: p.category, price: p.price, cost: p.cost ?? 0, stock: p.stock, taxable: p.taxable ?? true });
    setError(""); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || form.price === "") { setError("Name and price are required."); return; }
    setSaving(true); setError("");
    try {
      const body = {
        name: form.name, category: form.category,
        price: Number(form.price), cost: Number(form.cost) || 0,
        stock: Number(form.stock) || 0, taxable: form.taxable,
      };
      const res = editTarget
        ? await fetch(`${API}/merch/products/${editTarget._id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify(body) })
        : await fetch(`${API}/merch/products`, { method: "POST", headers: AUTH, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setError(d.error ?? "Failed."); return; }
      setShowForm(false);
      fetchProducts();
    } catch { setError("Network error."); }
    finally   { setSaving(false); }
  };

  const handleToggleActive = async p => {
    await fetch(`${API}/merch/products/${p._id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify({ active: !p.active }) });
    fetchProducts();
  };

  const handleDelete = async p => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    await fetch(`${API}/merch/products/${p._id}`, { method: "DELETE", headers: AUTH });
    fetchProducts();
  };

  const categories = [...new Set(products.map(p => p.category || "General"))];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="adm-roster-header">
        <h2 className="adm-section-title">Stock</h2>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>+ Add Product</button>
      </div>

      {showForm && (
        <div className="adm-create-form">
          <p className="adm-form-title">{editTarget ? "Edit Product" : "New Product"}</p>
          <div className="adm-form-grid">
            <label className="adm-form-label">Name
              <input className="adm-input" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Yoga Mat" />
            </label>
            <label className="adm-form-label">Category
              <input className="adm-input" value={form.category} onChange={e => set("category", e.target.value)} placeholder="e.g. Equipment" list="cat-list" />
              <datalist id="cat-list">{categories.map(c => <option key={c} value={c} />)}</datalist>
            </label>
            <label className="adm-form-label">Price ($)
              <input className="adm-input" type="number" min="0" step="0.01" autoComplete="off" value={form.price} onChange={e => set("price", e.target.value)} />
            </label>
            <label className="adm-form-label">Cost per unit ($)
              <input className="adm-input" type="number" min="0" step="0.01" autoComplete="off" value={form.cost} onChange={e => set("cost", e.target.value)} />
            </label>
            <label className="adm-form-label">Stock
              <input className="adm-input" type="number" min="0" value={form.stock} onChange={e => set("stock", e.target.value)} />
            </label>
            <label className="adm-form-label" style={{ alignSelf: "end" }}>
              <span><input type="checkbox" checked={form.taxable} onChange={e => set("taxable", e.target.checked)} />{" "}Charge sales tax</span>
            </label>
          </div>
          {error && <p className="adm-error">{error}</p>}
          <div className="adm-modal-actions" style={{ justifyContent: "flex-start", marginTop: "0.75rem" }}>
            <button className="adm-btn adm-btn--ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="adm-btn adm-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : editTarget ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </div>
      )}

      {loading && <p className="adm-loading">Loading…</p>}
      {!loading && products.length === 0 && <p className="adm-empty">No products yet.</p>}
      {!loading && products.length > 0 && (
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr><th>SKU</th><th>Product</th><th>Category</th><th>Price</th><th>Cost</th><th>Stock</th><th>Taxable</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p._id} className={!p.active ? "row-cancelled" : ""}>
                  <td><code>{p.sku ?? "—"}</code></td>
                  <td><strong>{p.name}</strong></td>
                  <td>{p.category || "—"}</td>
                  <td>{fmtMoney(p.price)}</td>
                  <td>{fmtMoney(p.cost ?? 0)}</td>
                  <td>
                    <span style={{ color: p.stock <= 0 ? "#e53e3e" : p.stock <= 3 ? "#b97a0a" : "inherit", fontWeight: p.stock <= 3 ? 700 : 400 }}>
                      {p.stock}
                    </span>
                  </td>
                  <td>{p.taxable ? "Yes" : "No"}</td>
                  <td><span className={`adm-status-badge ${p.active ? "status-confirmed" : "status-cancelled"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  <td>
                    <div className="adm-row-actions">
                      <button className="adm-btn adm-btn--ghost-sm" onClick={() => openEdit(p)}>Edit</button>
                      <button className="adm-btn adm-btn--ghost-sm" onClick={() => handleToggleActive(p)}>{p.active ? "Deactivate" : "Reactivate"}</button>
                      <button className="adm-btn-danger-sm" onClick={() => handleDelete(p)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Tab ────────────────────────────────────────────────
export default function MerchTab() {
  const { IS_OWNER } = useAdminApi();
  const [section, setSection] = useState("sell");
  const sections = [["sell", "Sell"], ["sales", IS_OWNER ? "Sales" : "My sales"], ...(IS_OWNER ? [["stock", "Stock"]] : [])];

  return (
    <div className="adm-tab-content">
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem" }}>
        {sections.map(([id, label]) => (
          <button key={id} className={`adm-btn ${section === id ? "adm-btn--primary" : "adm-btn--ghost"}`} onClick={() => setSection(id)}>
            {label}
          </button>
        ))}
      </div>
      {section === "sell"  && <SellSection isOwner={IS_OWNER} />}
      {section === "sales" && <SalesSection isOwner={IS_OWNER} />}
      {section === "stock" && IS_OWNER && <StockSection />}
    </div>
  );
}
